import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { useGoogleAPI } from "@jti/fe-clients";
import { Button } from "../../../../components/Button/Button";
import { Icon } from "../../../../components/Icon/Icon";
import SearchIcon from "../../../../icons/search.svg";
import TargetIcon from "../../../../icons/target.svg";
import { useConfig } from "../../../../providers/useConfig";
import { useTranslations } from "../../../../providers/useTranslations";
import { replaceStringPlaceholder } from "../../../../utils/replaceStringPlaceholder/replaceStringPlaceholder";
import { usePrizeLocatorContext } from "../../hooks";
import { PrizeLocatorDisplayTypeToggle } from "../PrizeLocatorDisplayTypeToggle";
import styles from "../../PrizeLocator.module.scss";
const ENTER_KEY = 13;
const DOWN_ARROW_KEY = 40;
const throwConfigError = () => {
    console.error("Wrong configuration");
    return "";
};
export const PrizeLocatorSearchBar = ({ count, map, loadMarkersFromApi, onChangeResultType, onLoadUserLocationMarker, hideCount, }) => {
    const { __ } = useTranslations();
    const _google = useGoogleAPI();
    const config = useConfig();
    const inputRef = useRef(null);
    const [focused, setFocused] = useState(false);
    const autocompleteRef = useRef(null);
    const { labels: { searchHeader, searchBar, searchButton, storesCount: storesCountText, currentPositionButtonText }, } = usePrizeLocatorContext();
    const options = {
        componentRestrictions: {
            country: config.ISO_COUNTRY_CODE !== undefined ? config.ISO_COUNTRY_CODE : throwConfigError(),
        },
        fields: ["geometry", "name"],
        types: ["geocode", "establishment"],
    };
    useEffect(() => {
        if (inputRef.current && map && _google) {
            autocompleteRef.current = new google.maps.places.Autocomplete(inputRef.current, options);
            autocompleteRef.current.bindTo("bounds", map);
            autocompleteRef.current.addListener("place_changed", onPlaceChanged);
            pacSelectFirst();
        }
    }, [_google, map]);
    const handleLocationError = (browserHasGeolocation) => {
        const message = browserHasGeolocation
            ? "Error: The Geolocation service failed."
            : "Error: Your browser doesn't support geolocation.";
        console.error(message);
    };
    const getCurrentLocationGoogleMaps = () => {
        if (navigator.geolocation && map) {
            navigator.geolocation.getCurrentPosition((position) => {
                const pos = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                };
                map.setCenter(pos);
                loadMarkersFromApi(map);
                onLoadUserLocationMarker(pos);
            }, () => {
                handleLocationError(true);
            });
        }
        else {
            handleLocationError(false);
        }
    };
    const createCustomEvent = (eventName, additionalData) => new CustomEvent(eventName, {
        detail: {
            additionalData,
        },
    });
    const handleSearchForm = (place) => {
        const event = createCustomEvent("tracking:event", {
            event: "store_locator_search",
            search_term: inputRef.current?.value,
        });
        document.dispatchEvent(event);
        if (place?.geometry) {
            if (place.geometry.viewport) {
                map?.fitBounds(place.geometry.viewport);
            }
            else {
                map?.setCenter(place.geometry.location);
            }
            setFocused(false);
        }
    };
    const handleSearchClick = () => {
        const noop = () => null;
        google.maps.event.trigger(inputRef.current, "focus", {});
        google.maps.event.trigger(inputRef.current, "keydown", {
            keyCode: DOWN_ARROW_KEY,
            stopPropagation: noop, // because these get called and throw errors
            preventDefault: noop,
        });
        google.maps.event.trigger(inputRef.current, "keydown", { keyCode: ENTER_KEY });
        google.maps.event.trigger(map, "zoom_changed", {});
    };
    const onPlaceChanged = () => {
        const place = autocompleteRef.current?.getPlace();
        if (!place?.geometry) {
            return;
        }
        handleSearchForm(place);
    };
    const pacSelectFirst = () => {
        if (inputRef.current) {
            const _addEventListener = inputRef.current.addEventListener;
            const addEventListenerWrapper = (type, listener) => {
                if (type === "keydown") {
                    const originalListener = listener;
                    listener = function (event) {
                        const suggestionSelected = document.querySelector(".pac-item-selected");
                        if (event.which === ENTER_KEY && !suggestionSelected) {
                            const simulatedEvent = new KeyboardEvent("keydown", {
                                keyCode: DOWN_ARROW_KEY,
                                which: DOWN_ARROW_KEY,
                            });
                            originalListener.apply(inputRef.current, [simulatedEvent]);
                        }
                        originalListener.apply(inputRef.current, [event]);
                    };
                }
                _addEventListener.apply(inputRef.current, [type, listener]);
            };
            inputRef.current.addEventListener = addEventListenerWrapper;
        }
    };
    const storesCount = replaceStringPlaceholder(storesCountText, [{ placeholder: "count", value: count }], false);
    return (_jsxs("div", { "data-testid": "storeLocatorSearchBar", className: styles.search, children: [_jsx("div", { className: styles.header, children: _jsx("div", { className: styles.textContainer, "data-testid": "text", "data-anchor": "anchor-text", children: _jsx("div", { className: styles.gridInner, "data-testid": "PolicyPageInnerText", children: searchHeader }) }) }), _jsxs("div", { className: styles.inputWrapper, children: [_jsxs("label", { className: styles.label, children: [_jsx(Icon, { className: styles.placeholderIcon, component: SearchIcon, height: "14px", width: "14px" }), _jsx("input", { ref: inputRef, className: classNames(styles.input, "pac-target-input", {
                                    [styles.inputActive]: focused,
                                }), type: "text", name: "searchInput", onChange: (event) => setFocused(!!event.target.value.length), onBlur: () => setFocused(false), placeholder: searchBar }), _jsx("button", { className: styles.mobileCurrentLocation, onClick: getCurrentLocationGoogleMaps, children: _jsx(Icon, { component: TargetIcon, height: "20px", width: "20px" }) })] }), _jsx(Button, { color: "primary", onClick: handleSearchClick, children: searchButton })] }), _jsxs("div", { className: styles.bottom, children: [_jsx("div", { className: styles.count, children: !hideCount ? storesCount : null }), _jsx("div", { className: styles.filtersWrapper, children: _jsxs("button", { className: styles.currentLocation, onClick: getCurrentLocationGoogleMaps, children: [_jsx(Icon, { className: styles.currentLocationIcon, component: TargetIcon, height: "20px", width: "20px" }), currentPositionButtonText] }) }), _jsx(PrizeLocatorDisplayTypeToggle, { name: "viewChange", id: "prizeLocatorSwitch", mapLabel: __("storelocator.mobile_view_switch.map"), listLabel: __("storelocator.mobile_view_switch.list"), onChange: (event) => onChangeResultType(event.target.checked ? "list" : "map") })] })] }));
};
//# sourceMappingURL=PrizeLocatorSearchBar.js.map